import React, {
  useCallback,
  useContext,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState
} from 'react'

import cx from 'classnames'
import { createEditor, Descendant, Range, Transforms } from 'slate'
import { withHistory } from 'slate-history'
import { Editable, ReactEditor, Slate, withReact } from 'slate-react'

import { Avatar, Button, IconButton, ToolTip } from '@driftt/tide-core'

import { createComment } from 'api'
import { CurrentUserContext } from 'context/currentUserContext'
import { OrgUsersContext } from 'context/orgUsersContext'
import { Comment } from 'modules/accountActivity/domain'
import {
  clearText,
  getBoundingRectangle,
  getStartCoordinates,
  INITIAL_COMPOSER_VALUE,
  moveCursorToEnd,
  moveCursorToStart,
  serializeToString,
  withMentions
} from 'utils/comments'
import {
  getMentionOptions,
  insertMention,
  parseMentionedUsers,
  parseMentionTarget
} from 'utils/mentions'

import Mention from './Mention'

import styles from './styles.module.css'

const MENTION_OPTION_ITEM_HEIGHT = 48
const MAX_MENTION_OPTIONS_VISIBLE = 4
const POPPER_LEFT_OFFSET_PX = 4

interface CommentComposerProps {
  insightId: number
  onCommentCreated: (comment: Comment) => void
  autofocus?: boolean
  parentCommentId?: string
  className?: string
}

const Element = (props: any) => {
  const { attributes, children, element } = props
  switch (element.type) {
    case 'mention':
      return <Mention {...props} />
    default:
      return <p {...attributes}>{children}</p>
  }
}

const renderElement = (props: any) => <Element {...props} />

const CommentComposer = React.forwardRef<any, CommentComposerProps>(
  ({ insightId, onCommentCreated, autofocus = true, parentCommentId, className }, ref) => {
    const referenceElement = useRef<HTMLDivElement>(null)
    const popperElement = useRef<HTMLDivElement>(null)

    const editor = useMemo(() => withMentions(withHistory(withReact(createEditor()))), [])
    const [composerValue, setComposerValue] = useState<Descendant[]>(INITIAL_COMPOSER_VALUE)
    const stringValue = useMemo(() => serializeToString(composerValue), [composerValue])
    const taggedUsers = useMemo(() => parseMentionedUsers(composerValue), [composerValue])
    const shouldShowMentionHint = useMemo(
      () => !parentCommentId && stringValue?.length > 3 && taggedUsers?.length === 0,
      [parentCommentId, stringValue.length, taggedUsers.length]
    )

    const [mentionTarget, setMentionTarget] = useState<Range | undefined>()
    const [mentionOptionIndex, setMentionOptionIndex] = useState(0)
    const [mentionSearch, setMentionSearch] = useState('')

    const currentUser = useContext(CurrentUserContext)
    const orgUsersById = useContext(OrgUsersContext)

    const mentionOptions = useMemo(() => getMentionOptions(orgUsersById, mentionSearch), [
      orgUsersById,
      mentionSearch
    ])

    const onMentionOptionSelected = useCallback(
      (mentionLabel: string, userId: number) => {
        if (mentionTarget) {
          Transforms.select(editor, mentionTarget)
          insertMention(editor, mentionLabel, userId)
          setMentionTarget(undefined)
        }
      },
      [editor, mentionTarget]
    )

    const handleTagUserClicked = useCallback(() => {
      ReactEditor.focus(editor)
      if (!editor.selection) {
        moveCursorToEnd(editor)
      }

      const lastCharacter = stringValue[stringValue.length - 1]
      if (stringValue.length === 0 || stringValue === '' || lastCharacter.match(/\s/)) {
        editor.insertText('@')
      } else if (lastCharacter && lastCharacter !== '@') {
        editor.insertText(' @')
      }
    }, [editor, stringValue])

    const handleCommentSubmit = useCallback(() => {
      createComment({
        body: JSON.stringify(composerValue),
        resourceId: `FEED_CARD:${insightId}`,
        parentCommentId,
        taggedUsers
      })
        .then(res => res.data)
        .then(onCommentCreated)
      clearText(editor, setComposerValue)
    }, [composerValue, insightId, parentCommentId, taggedUsers, onCommentCreated, editor])

    const onKeyDown = useCallback(
      (event: React.KeyboardEvent) => {
        if (mentionTarget) {
          switch (event.key) {
            case 'ArrowDown':
              event.preventDefault()
              if (mentionOptionIndex < mentionOptions.length - 1) {
                const nextIndex = mentionOptionIndex + 1
                setMentionOptionIndex(nextIndex)
                if (popperElement.current && nextIndex >= MAX_MENTION_OPTIONS_VISIBLE) {
                  popperElement.current.scrollTop += MENTION_OPTION_ITEM_HEIGHT
                }
              }
              break
            case 'ArrowUp':
              event.preventDefault()
              if (mentionOptionIndex > 0) {
                const prevIndex = mentionOptionIndex - 1
                setMentionOptionIndex(prevIndex)
                if (
                  popperElement.current &&
                  prevIndex < mentionOptions.length - MAX_MENTION_OPTIONS_VISIBLE
                ) {
                  popperElement.current.scrollTop -= MENTION_OPTION_ITEM_HEIGHT
                }
              }
              break
            case 'Tab':
            case 'Enter': {
              event.preventDefault()
              const option = mentionOptions[mentionOptionIndex]
              onMentionOptionSelected(option.label, option.user?.id || 0)
              return
            }
            case 'Escape':
              event.preventDefault()
              setMentionTarget(undefined)
              break
          }
        }

        if (event.key === 'Enter' && !event.shiftKey) {
          event.preventDefault()
          handleCommentSubmit()
        }
      },
      [
        mentionOptionIndex,
        mentionOptions,
        mentionTarget,
        onMentionOptionSelected,
        popperElement,
        handleCommentSubmit
      ]
    )

    useImperativeHandle(ref, () => ({
      focus() {
        ReactEditor.focus(editor)
        moveCursorToStart(editor)
      }
    }))

    useEffect(() => {
      if (autofocus) {
        ReactEditor.focus(editor)
        moveCursorToStart(editor)
      }
    }, [autofocus, editor])

    useEffect(() => {
      if (popperElement.current && referenceElement.current && mentionTarget) {
        const popper = popperElement.current
        const composer = referenceElement.current

        const mentionCoordinates = getBoundingRectangle(editor, mentionTarget)
        const startCoordinates = getStartCoordinates(editor)
        const yScroll = composer.querySelector(`.${styles.commentTextInput}`)?.scrollTop || 0

        popper.style.top = `${mentionCoordinates.top -
          startCoordinates.top -
          popper.clientHeight -
          yScroll}px`
        popper.style.left = `${mentionCoordinates.left -
          startCoordinates.left +
          POPPER_LEFT_OFFSET_PX}px`
        popper.style.visibility = 'visible'
      }
    }, [editor, mentionTarget, mentionOptions])

    return (
      <div className={cx(styles.commentComposer, className)}>
        <Avatar user={currentUser} />
        <Slate
          value={composerValue}
          editor={editor}
          onChange={value => {
            setComposerValue(value)
            const target = parseMentionTarget(editor)
            if (target) {
              const { mentionTargetRange, mentionSearchText } = target
              setMentionTarget(mentionTargetRange)
              setMentionSearch(mentionSearchText)
              setMentionOptionIndex(0)
            } else {
              setMentionTarget(undefined)
            }
          }}>
          <div className={styles.inputAndPost}>
            <div ref={referenceElement} className={styles.commentTextInputWrapper}>
              <Editable
                className={styles.commentTextInput}
                spellCheck={false}
                placeholder={parentCommentId ? 'Write a reply...' : 'Write a comment...'}
                onKeyDown={onKeyDown}
                renderElement={renderElement}
              />
              <ToolTip
                className={styles.tagUserToolTip}
                flow="up"
                component={
                  <>
                    <p>Tag a teammate in your comment</p>
                    <p>to send them a notification</p>
                  </>
                }>
                <IconButton
                  icon="shopping-tag"
                  className={styles.tagUserAction}
                  onClick={handleTagUserClicked}
                />
              </ToolTip>
              {mentionTarget && mentionOptions.length > 0 && (
                <div ref={popperElement} className={styles.mentionOptionsList}>
                  {mentionOptions.map((option, i) => (
                    <div
                      key={option.user?.id}
                      className={cx(styles.mentionOption, {
                        [styles.focusedMentionOption]: i === mentionOptionIndex
                      })}
                      onMouseDown={event => {
                        event.preventDefault()
                        onMentionOptionSelected(option.label, option.user?.id || 0)
                      }}>
                      <Avatar user={option.user} />
                      {option.label}
                    </div>
                  ))}
                </div>
              )}
            </div>
            {shouldShowMentionHint && (
              <div className={styles.mentionHint}>
                Consider adding a tag to your comment to notify your teammate(s)
              </div>
            )}
            {stringValue !== '' && (
              <Button
                type="primary"
                size="small"
                onClick={handleCommentSubmit}
                className={styles.postCommentButton}>
                Post
              </Button>
            )}
          </div>
        </Slate>
      </div>
    )
  }
)

export default CommentComposer
